// Admin Portal: org-owned capabilities editor (2026-08-22).
// Two modes. "Org defaults" edits orgs/{orgId}.capabilityDefaults: a checked
// row is allowed (absent key), an unchecked row writes `false`. "Per member"
// edits one member's capabilities map with three states per row: Inherit
// (no key, shows what it inherits), Allow (true), Deny (false). Writes go
// through llm-proxy (validated, audit-logged); see src/firebase/capabilities.js.
import React, { useCallback, useEffect, useState } from 'react';
import { groupedCapabilities } from '../../config/capabilities';
import { getOrg, getOrgMembers } from '../../firebase/orgs';
import { setOrgCapabilityDefaults, setMemberCapabilities } from '../../firebase/capabilities';
import { useAuth } from '../../context/AuthContext';

const card = { background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 10, padding: '28px 32px', marginBottom: 28 };
const sectionHeader = { color: '#e2e8f0', fontWeight: 700, fontSize: '1rem', marginBottom: 20, letterSpacing: 0.5, textTransform: 'uppercase' };
const groupHeader = { color: '#94a3b8', fontWeight: 600, fontSize: 13, margin: '16px 0 6px' };
const row = { display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid #0a1120' };
const btn = (color, active = false) => ({
  background: active ? `${color}22` : 'transparent', border: `1px solid ${active ? color : '#334155'}`,
  borderRadius: 6, color: active ? color : '#64748b', fontWeight: 600, fontSize: 12, padding: '4px 10px', cursor: 'pointer',
});
const inputStyle = { background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 13 };

function HeavyBadge() {
  return <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: '#f59e0b22', border: '1px solid #f59e0b55', borderRadius: 4, padding: '1px 6px' }}>Heavy</span>;
}

export default function CapabilitiesSection({ orgId }) {
  const { refreshCapabilities } = useAuth() || {};
  const [mode, setMode] = useState('org');
  const [defaults, setDefaults] = useState({});
  const [loadedDefaults, setLoadedDefaults] = useState({});
  const [members, setMembers] = useState([]);
  const [memberEmail, setMemberEmail] = useState('');
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [org, ms] = await Promise.all([getOrg(orgId), getOrgMembers(orgId)]);
      const d = (org && org.capabilityDefaults) || {};
      setDefaults(d);
      setLoadedDefaults(d);
      setMembers(ms || []);
    } catch (e) {
      setError(e.message || 'Failed to load capabilities');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);
  useEffect(() => {
    const m = members.find((x) => x.email === memberEmail);
    setOverrides((m && m.capabilities) || {});
  }, [memberEmail, members]);

  const defaultAllowed = (id) => defaults[id] !== false;
  const inheritedAllowed = (id) => loadedDefaults[id] !== false;
  const toggleDefault = (id) => setDefaults((prev) => {
    const next = { ...prev };
    if (next[id] === false) delete next[id]; else next[id] = false;
    return next;
  });
  const overrideState = (id) => (overrides[id] === true ? 'allow' : overrides[id] === false ? 'deny' : 'inherit');
  const setOverride = (id, state) => setOverrides((prev) => {
    const next = { ...prev };
    if (state === 'inherit') delete next[id]; else next[id] = state === 'allow';
    return next;
  });

  const save = async () => {
    setSaving(true); setError(''); setSaved('');
    try {
      if (mode === 'org') await setOrgCapabilityDefaults(orgId, defaults);
      else await setMemberCapabilities(orgId, memberEmail, overrides);
      setSaved('Saved.');
      await load();
      if (typeof refreshCapabilities === 'function') refreshCapabilities();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={sectionHeader}>Capabilities</div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>
        Everything is allowed unless you turn it off. Org defaults apply to every member; per-member overrides win over the defaults.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <button type="button" style={btn('#4ade80', mode === 'org')} onClick={() => { setMode('org'); setSaved(''); setError(''); }}>Org defaults</button>
        <button type="button" style={btn('#38bdf8', mode === 'member')} onClick={() => { setMode('member'); setSaved(''); setError(''); }}>Per member</button>
        {mode === 'member' && (
          <label style={{ color: '#94a3b8', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
            Member
            <select style={inputStyle} value={memberEmail} onChange={(e) => { setSaved(''); setError(''); setMemberEmail(e.target.value); }}>
              <option value="">Select a member</option>
              {members.map((m) => <option key={m.email} value={m.email}>{m.email}</option>)}
            </select>
          </label>
        )}
      </div>

      {loading ? <div style={{ color: '#64748b' }}>Loading capabilities...</div> : groupedCapabilities().map((g) => (
        <div key={g.group}>
          <div style={groupHeader}>{g.label}</div>
          {g.items.map((c) => {
            const inherited = defaultAllowed(c.id);
            return (
              <div key={c.id} style={row} data-testid={`cap-${c.id}`}>
                {mode === 'org' ? (
                  <input type="checkbox" checked={inherited} onChange={() => toggleDefault(c.id)} aria-label={c.label} />
                ) : (
                  <span style={{ display: 'flex', gap: 4 }}>
                    {['inherit', 'allow', 'deny'].map((s) => (
                      <button key={s} type="button" aria-pressed={overrideState(c.id) === s}
                        style={btn(s === 'deny' ? '#f87171' : s === 'allow' ? '#4ade80' : '#94a3b8', overrideState(c.id) === s)}
                        disabled={!memberEmail}
                        onClick={() => setOverride(c.id, s)}>
                        {s === 'inherit' ? 'Inherit' : s === 'allow' ? 'Allow' : 'Deny'}
                      </button>
                    ))}
                  </span>
                )}
                <span style={{ color: '#e2e8f0', fontSize: 13, minWidth: 260 }}>{c.label}</span>
                {c.cost_hint === 'heavy' && <HeavyBadge />}
                <span style={{ color: '#64748b', fontSize: 12, flex: 1 }}>{c.description}</span>
                {mode === 'member' && (
                  <span style={{ color: '#64748b', fontSize: 11 }}>inherits: {inheritedAllowed(c.id) ? 'allowed' : 'denied'}</span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
        <button type="button" style={btn('#4ade80', true)} disabled={saving || loading || (mode === 'member' && !memberEmail)} onClick={save}>
          {mode === 'org' ? 'Save org defaults' : 'Save member overrides'}
        </button>
        {saved && <span style={{ color: '#4ade80', fontSize: 13 }}>{saved}</span>}
        {error && <span style={{ color: '#f87171', fontSize: 13 }}>{error}</span>}
      </div>
    </div>
  );
}
